import {
  CallQueueContactOutSchema,
  CallQueueContactOutSchema2,
  PagedCallQueueContactOutSchema,
  PagedCallQueueContactOutSchema2,
  UserContactOutSchema,
} from "@/api/client";
import { handleError } from "@/api/handleError";
import {
  getAgentCallQueueItems,
  getAgentCallQueues,
} from "@/api/requests/callQueue";
import { getCallQueueItemsFromLink } from "@/api/requests/misc";
import {
  getAgentCallQueueAcrossCampaigns,
  getAgentCallQueueInCampaign,
  getAgentCallQueueItemsInCampaign,
} from "@/api/requests/salesCampaign";
import { useSalesCampaignAddOnStore } from "@/stores/add-ons/sales-campaign.store";
import { useBusinessStore } from "@/stores/businesses.store";
import { useContactStore } from "@/stores/contact.store";
import { useTeamStore } from "@/stores/team-member.store";
import { useUsersStore } from "@/stores/users.store";
import { computed, ref } from "vue";
import { getSingleContact, handleSearchContacts } from "./contacts";
import { removeContactFromQueueCampaign } from "./queries/add-ons/sales-campaign";
import {
  addContactToQueue,
  removeContactFromQueue,
  reorderCallQueueAgent,
} from "./queries/callQueue";
import type {
  AddContactToQueuePayload,
  QueueItemType,
  updateQueuePointerPayload,
} from "./types/callQueue";

export const useCallQueue = {
  callQueue: ref<QueueItemType[] | null | undefined>(null),
  callQueueData: ref<
    PagedCallQueueContactOutSchema | PagedCallQueueContactOutSchema2 | null
  >(null),
  showMoreContact: ref<unknown>(""),
  showAddQueueModal: ref(false),

  showAddNewContactQueueModal: ref(false),

  callQueueCopy: ref<QueueItemType[] | null | undefined>([]),

  activeContact: ref<QueueItemType | undefined>(),

  activeContactDetails: ref<
    { contact: UserContactOutSchema | undefined } | null | undefined
  >(null),

  activeContactAvatarColor: ref(""),

  nextActiveContact: ref<QueueItemType | undefined>(),

  callStateName: ref<"Start" | "Pause" | "Resume">("Start"),

  isSearchingContact: ref(false),

  isFetching: ref(false),

  handleResponse: async (res: QueueItemType[] | null) => {
    useCallQueue.callQueue.value = res || [];

    useCallQueue.callQueueCopy.value = [...useCallQueue.callQueue.value];
    useCallQueue.activeContact.value = useCallQueue.callQueue.value[0] || {};
    if (useCallQueue.callQueue.value.length) {
      // only run this when callqueue is not empty causing an undefined error in prod
      useCallQueue.activeContactDetails.value = await getSingleContact(
        useCallQueue.callQueue.value[0]?.contact_id
      );
    }

    useCallQueue.nextActiveContact.value =
      useCallQueue.callQueue.value[1] || {};

    if (useCallQueue.callQueue.value.length < 1) {
      useCallQueue.callStateName.value = "Start";
    }
  },

  listCallQueue: async () => {
    const contactStore = useContactStore();
    const teamStore = useTeamStore();
    const businessStore = useBusinessStore();

    const businessId = businessStore.currentBusinessId;
    const callQueueId = contactStore.callQueueId;
    const agentId = teamStore.selectedTeamMember;

    try {
      if (!businessId || !callQueueId || !agentId) {
        throw new Error("Missing business id or user id or call queue id");
      }

      useCallQueue.isFetching.value = true;

      const res = await getAgentCallQueueItems({
        businessId,
        queueId: callQueueId,
        userId: agentId,
      });

      await useCallQueue.handleResponse(res.data.data);
    } catch (error) {
      handleError({ error, fallbackMessage: "Error fetching call queue" });
    } finally {
      useCallQueue.isFetching.value = false;
    }
  },

  listCallQueueAgent: async () => {
    const businessStore = useBusinessStore();
    const contactStore = useContactStore();
    const salesCampaignAddonStore = useSalesCampaignAddOnStore();

    const hasAccessToCampaign = salesCampaignAddonStore.getAgentHasAccess;
    const businessId = businessStore.currentBusinessId;
    const userId = businessStore.currentUserId;
    const campaignId = salesCampaignAddonStore.currentAgentViewingCampaign?.id;

    try {
      if (!businessId || !userId) {
        throw new Error("Missing business id or user id");
      }

      useCallQueue.isFetching.value = true;

      let response:
        | CallQueueContactOutSchema[]
        | CallQueueContactOutSchema2[]
        | null = null;

      if (!hasAccessToCampaign || !campaignId) {
        const callQueueResponse = await getAgentCallQueues({
          businessId,
          userId,
        });

        contactStore.callQueueId = callQueueResponse.data[0].id;

        if (!contactStore.callQueueId) {
          throw new Error("No call queue found");
        }

        const callQueueItemsResponse = await getAgentCallQueueItems({
          businessId,
          queueId: contactStore.callQueueId,
          userId,
          pageSize: 100,
        });

        response = callQueueItemsResponse.data.data;

        useCallQueue.callQueueData.value = callQueueItemsResponse.data;
      } else {
        const callQueueCampaignsResponse =
          await getAgentCallQueueAcrossCampaigns({
            businessId,
            agentId: userId,
          });

        contactStore.callQueueId = callQueueCampaignsResponse.data[0].id;

        if (!contactStore.callQueueId) {
          throw new Error("No call queue found");
        }

        const [callQueueInCampaignResponse, callQueueItemsInCampaignResponse] =
          await Promise.all([
            getAgentCallQueueInCampaign({
              businessId,
              agentId: userId,
              queueId: contactStore.callQueueId,
              campaignId: campaignId,
            }),
            getAgentCallQueueItemsInCampaign({
              businessId,
              agentId: userId,
              queueId: contactStore.callQueueId,
              campaignId: campaignId,
              pageSize: 100,
            }),
          ]);

        salesCampaignAddonStore.currentAgentViewingCallQueueDetails =
          callQueueInCampaignResponse.data;

        response = callQueueItemsInCampaignResponse.data.data;

        useCallQueue.callQueueData.value =
          callQueueItemsInCampaignResponse.data;
      }

      await useCallQueue.handleResponse(response);
    } catch (error) {
      handleError({
        error,
        fallbackMessage: "Error fetching call queue",
      });
    } finally {
      useCallQueue.isFetching.value = false;
    }
  },
  loadMore: async () => {
    const contactStore = useContactStore();
    const teamStore = useTeamStore();
    const businessStore = useBusinessStore();

    const businessId = businessStore.currentBusinessId;
    const callQueueId = contactStore.callQueueId;
    const agentId = teamStore.selectedTeamMember;

    if (!useCallQueue.callQueueData?.value?.next) {
      return;
    }

    try {
      if (!businessId || !callQueueId || !agentId) {
        throw new Error("Missing business id or user id or call queue id");
      }

      useCallQueue.showMoreContact.value = true;

      const callQueueItemsResponse = await getAgentCallQueueItems({
        businessId,
        queueId: callQueueId,
        userId: agentId,
        pageIndex: useCallQueue.callQueueData.value?.page_index + 1,
      });

      useCallQueue.callQueueData.value.next = callQueueItemsResponse.data?.next;

      useCallQueue.callQueue.value = [
        ...(useCallQueue.callQueue.value || []),
        ...callQueueItemsResponse.data.data,
      ];

      useCallQueue.callQueueCopy.value = useCallQueue.callQueue.value;
    } catch (error) {
      handleError({
        error,
        fallbackMessage: "Error loading more call queue",
      });
    } finally {
      useCallQueue.showMoreContact.value = false;
    }
  },

  addContactToQueue: async (payload: AddContactToQueuePayload) => {
    try {
      const callQueueId = useContactStore().callQueueId;
      const userStore = useUsersStore();

      if (!callQueueId) {
        throw new Error("Missing call queue id");
      }

      await addContactToQueue(payload, callQueueId);

      if (!userStore.canAccessManagerView) {
        await useCallQueue.listCallQueueAgent();
      } else {
        await useCallQueue.listCallQueue();
      }
    } catch (error) {
      handleError({ error });
    }
  },

  removeFromCallQueue: async () => {
    try {
      const salesCampaignAddonStore = useSalesCampaignAddOnStore();

      const payload = {
        contacts: useCallQueue.activeContact.value?.contact_id
          ? [useCallQueue.activeContact.value?.contact_id]
          : [],
      };

      const callQueueId = useContactStore().callQueueId || undefined;

      const businessStore = useBusinessStore();

      if (salesCampaignAddonStore.currentAgentViewingCampaign?.id) {
        const activeContact = useCallQueue.activeContact.value;

        const activeContactId =
          activeContact && "id" in activeContact
            ? activeContact?.id
            : undefined;

        if (!activeContactId) {
          throw new Error("Missing active contact id");
        }

        await removeContactFromQueueCampaign(
          salesCampaignAddonStore.currentAgentViewingCampaign.id,
          callQueueId,
          activeContactId
        );
      }

      await removeContactFromQueue(
        payload,
        callQueueId,
        businessStore.currentUserId
      );

      await useCallQueue.listCallQueueAgent();
    } catch (error) {
      handleError({ error });
    }
  },

  updateActiveContact: async () => {
    try {
      useCallQueue.activeContact.value = useCallQueue.callQueue?.value?.[0];

      const contactId = useCallQueue.callQueue.value?.[0]?.contact_id;

      if (!contactId) {
        throw new Error("Missing contact id");
      }

      useCallQueue.activeContactDetails.value =
        await getSingleContact(contactId);

      useCallQueue.nextActiveContact.value = useCallQueue.callQueue.value?.[1];

      useCallQueue.callQueueCopy.value = [
        ...(useCallQueue.callQueue.value || []),
      ];
    } catch (error) {
      handleError({ error });
    }
  },

  handleUpdateQueuePointer: async (payload: updateQueuePointerPayload) => {
    const contactStore = useContactStore();
    const callQueueId = contactStore.callQueueId;

    try {
      if (!callQueueId) {
        throw new Error("Missing call queue id");
      }

      await reorderCallQueueAgent(payload, callQueueId.toString());
    } catch (error) {
      handleError({ error });
    }
  },

  callQueueTotal: computed((): number | undefined => {
    return useCallQueue.callQueueData.value?.total;
  }),

  handleDragEnd: async (event: { oldIndex: number; newIndex: number }) => {
    const { oldIndex, newIndex } = event;

    const payload = {
      action: newIndex > oldIndex ? "after" : "before",
      moved_contact_id:
        useCallQueue.callQueue.value?.[oldIndex].contact_id || "",
      cursor_contact_id:
        useCallQueue.callQueue.value?.[newIndex].contact_id || "",
    };

    await useCallQueue.handleUpdateQueuePointer(payload);

    useCallQueue.setUpdatedQueue(oldIndex, newIndex);
  },

  setUpdatedQueue: (oldIndex: number, newIndex: number) => {
    // Copy the callQueue.value array

    const updatedCallQueue = [...(useCallQueue.callQueue.value || [])];

    // Remove the element at the old index
    const [movedElement] = updatedCallQueue.splice(oldIndex, 1);

    // Adjust the newIndex if the oldIndex is before it
    const adjustedNewIndex = newIndex;

    // Insert the moved element at the adjusted newIndex
    updatedCallQueue.splice(adjustedNewIndex, 0, movedElement);
    useCallQueue.callQueue.value = updatedCallQueue;
    useCallQueue.callQueueCopy.value = updatedCallQueue;
    void useCallQueue.updateActiveContact();
  },
};

export const getContactFromSavedContact = async (phoneNumber: string) => {
  try {
    useCallQueue.isSearchingContact.value = true;
    // const cleanedContactNumber = contact.contact_number.replace(
    //   /^(?:\+234|\+254|0)/,
    //   ""
    // );
    const cleanedPhoneNumber = phoneNumber.replace(/^(?:\+234|\+254|0)/, "0");
    const result = (await handleSearchContacts(cleanedPhoneNumber)).contacts;
    useCallQueue.isSearchingContact.value = false;
    return result;
    // return allContacts.find((contact) => {
    //   return cleanedContactNumber.includes(cleanedPhoneNumber);
    // });
  } catch (error) {
    handleError({ error });
  }
};

export const getCallQueue = async () => {
  await useCallQueue.listCallQueue();
  return useCallQueue.callQueue.value;
};

export const isNumberValid = (number: string) => {
  const cleanedContactNumber = number.replace(/^(?:\+234|\+254|0)/, "");
  if (cleanedContactNumber.length == 10 && +cleanedContactNumber) {
    return true;
  } else {
    return false;
  }
};

export const cleanPayload = <T>(obj: T) => {
  //function to remove object properties with null as keys
  for (const propName in obj) {
    if (
      obj[propName] === null ||
      obj[propName] === undefined ||
      obj[propName] === ""
    ) {
      delete obj[propName];
    }
  }
  return obj;
};

export const getCallQueueItems = async (callQueueId?: number) => {
  const contactStore = useContactStore();
  const businessStore = useBusinessStore();
  const teamStore = useTeamStore();

  const selectedCallQueueId = callQueueId || contactStore.callQueueId;
  const businessId = businessStore.currentBusinessId;
  const userId = teamStore.selectedTeamMember;

  try {
    if (!selectedCallQueueId) {
      throw new Error("Missing call queue id");
    }

    if (!businessId) {
      throw new Error("Missing business id");
    }

    if (!userId) {
      throw new Error("Missing user id");
    }

    const response = await getAgentCallQueueItems({
      businessId,
      userId,
      queueId: selectedCallQueueId,
    });

    return response.data;
  } catch (error) {
    handleError({ error });
  }
};

export const getCallQueueItemsForTeamMember = async (
  id?: number | null,
  url?: string
) => {
  if (url) {
    const response = await getCallQueueItemsFromLink({
      link: url,
    });

    return response.data || [];
  }

  const callQueueId = Number(id);

  if (isNaN(callQueueId)) {
    throw new Error("Invalid call queue id");
  }

  return await getCallQueueItems(callQueueId);
};

export const addContactsToTeamMemberCallQueue = async (
  callQueueId: number,
  payload: AddContactToQueuePayload,
  initiator?: string
) => {
  try {
    await addContactToQueue(payload, callQueueId);

    if (initiator !== "fromContactsQueue") {
      return await getCallQueueItemsForTeamMember(callQueueId);
    }
  } catch (error) {
    handleError({ error });
  }
};

export function distributeContactsEvenly(total: number, length: number) {
  const result: number[] = new Array(length).fill(0) as number[]; // Create an array of the given length, initialized with zeros
  const baseValue = Math.floor(total / length); // Determine the base value for each item
  const remainder = total % length; // Calculate the remainder

  // Distribute the base value
  for (let i = 0; i < length; i++) {
    result[i] = baseValue;
  }

  // Distribute the remainder
  for (let i = 0; i < remainder; i++) {
    result[i]++;
  }

  return result;
}
